import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [trainees, setTrainees] = useState(null);
  const [interests, setInterests] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, t, i] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/trainees"),
        api.get("/admin/employment-interests"),
      ]);
      setStats(s.data);
      setTrainees(t.data);
      setInterests(i.data);
    })();
  }, []);

  if (!stats || !trainees || !interests) return <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 label-eyebrow">Loading admin data…</div>;

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-dashboard">
      <div className="label-eyebrow mb-3">Administrator · {user.email}</div>
      <h1 className="font-serif text-5xl text-ink mb-10">Program overview</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-stone bg-white mb-12">
        {[
          ["Trainees enrolled", stats.total_trainees],
          ["Quiz attempts", stats.total_quiz_attempts],
          ["Certificates issued", stats.total_certificates],
          ["GH employment interests", interests.total],
        ].map(([label, value], i) => (
          <div key={i} className={`p-6 ${i > 0 ? "border-l border-stone" : ""}`}>
            <div className="label-eyebrow mb-2">{label}</div>
            <div className="font-serif text-4xl text-ink">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Module completions */}
        <div className="lg:col-span-1">
          <div className="editorial-rule mb-6">Module completions</div>
          <ul className="space-y-3" data-testid="module-completions-list">
            {stats.completions_per_module.map((m, i) => (
              <li key={m.module_id} className="flex items-center justify-between text-sm border-b border-stone/70 pb-3">
                <span className="text-ink">{String(i + 1).padStart(2, "0")} · {m.title}</span>
                <span className="font-mono text-ink">{m.completions}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Trainees */}
        <div className="lg:col-span-2">
          <div className="editorial-rule mb-6">Trainee roster · {trainees.trainees.length}</div>
          {trainees.trainees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No trainees yet.</p>
          ) : (
            <div className="border border-stone bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="trainees-table">
                <thead className="bg-cream">
                  <tr className="text-left">
                    <th className="label-eyebrow py-3 px-4">Name</th>
                    <th className="label-eyebrow py-3 px-4">Email</th>
                    <th className="label-eyebrow py-3 px-4">Modules</th>
                    <th className="label-eyebrow py-3 px-4">Attempts</th>
                    <th className="label-eyebrow py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trainees.trainees.map((t) => (
                    <tr key={t.id} className="border-t border-stone" data-testid={`trainee-row-${t.id}`}>
                      <td className="py-3 px-4 text-ink">{t.name}</td>
                      <td className="py-3 px-4 text-muted-foreground">{t.email}</td>
                      <td className="py-3 px-4 font-mono text-ink">{t.modules_completed}/{t.modules_total}</td>
                      <td className="py-3 px-4 font-mono text-ink">{t.quiz_attempts}</td>
                      <td className="py-3 px-4">
                        {t.certified ? (
                          <span className="text-sage">Certified · {t.certificate_id}</span>
                        ) : (
                          <span className="text-muted-foreground">In progress</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Employment Interests — Generational Health pipeline */}
      <div className="mt-12">
        <div className="editorial-rule mb-6">Generational Health employment interests · {interests.total}</div>
        {interests.total === 0 ? (
          <p className="text-sm text-muted-foreground">No employment interest submissions yet.</p>
        ) : (
          <div className="border border-stone bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="gh-interests-table">
              <thead className="bg-cream">
                <tr className="text-left">
                  <th className="label-eyebrow py-3 px-4">Submitted</th>
                  <th className="label-eyebrow py-3 px-4">Name</th>
                  <th className="label-eyebrow py-3 px-4">Email</th>
                  <th className="label-eyebrow py-3 px-4">Phone</th>
                  <th className="label-eyebrow py-3 px-4">City</th>
                  <th className="label-eyebrow py-3 px-4">Role</th>
                  <th className="label-eyebrow py-3 px-4">Trainee?</th>
                </tr>
              </thead>
              <tbody>
                {interests.interests.map((row) => (
                  <tr key={row.id} className="border-t border-stone" data-testid={`gh-interest-row-${row.id}`}>
                    <td className="py-3 px-4 text-muted-foreground font-mono text-xs">
                      {new Date(row.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="py-3 px-4 text-ink">{row.name}</td>
                    <td className="py-3 px-4 text-muted-foreground">{row.email}</td>
                    <td className="py-3 px-4 text-muted-foreground">{row.phone || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground">{row.city || "—"}</td>
                    <td className="py-3 px-4 text-ink">{row.role_interest || "—"}</td>
                    <td className="py-3 px-4">
                      {row.is_trainee ? <span className="text-sage">Yes</span> : <span className="text-muted-foreground">No</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
